import { ArrowLeftOutlined } from "@ant-design/icons"
import { Drawer } from "antd"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { generatePersona } from "~/api/message-genie"
import { getSinglePersonBriefStream } from "~/api/pre-meeting"
import DownloadPreMeeting from "~/containers/knowledge-os/pre-meeting/download-pre-meeting"
import { PeopleLoader } from "~/containers/knowledge-os/pre-meeting/loader"
import SharePreMeeting from "~/containers/knowledge-os/pre-meeting/share-pre-meeting"
import { useGetPersonData, useGetPreMeetingBrief } from "~/hooks/pre-meeting"
import useGlobalStore from "~/store/global"
import { ITokenInfo } from "~/types/global"
import { applyPersonBriefFrame } from "~/utils/person-brief-frames"
import { sleep } from "~/utils/sleep"

import BriefCombo from "./BriefCombo"
import { formatNamesForFile } from "./formatNamesForFile"
import MultiplePeopleInformation from "./MultiplePeopleInformation"
import QueryFail from "./QueryFail"

// main code
const PreMeetingBriefDetails = ({
  data,
  urls,
  open,
  setOpen,
  sessionId,
  timestamp
}: {
  data: any
  urls: any
  open: boolean
  setOpen: (arg: boolean) => void
  sessionId: string
  timestamp: string
}) => {
  const { t } = useTranslation()
  const featureFlags = useGlobalStore((store) => store.user?.feature_flags)
  const linkedinUrls = urls.map((url: any) => url.linkedin_url_raw)
  const peopleNames = urls.map((url: any) => url.name)
  const tokenInfo = useGlobalStore((state) => state.tokenInfo)
  const isSingle = linkedinUrls.length === 1
  const linkedinData: Record<
    string,
    { name: string; organization_id: string; organization_name: string }
  > = Object.fromEntries(
    urls.map((url: any) => [
      url.linkedin_url_raw,
      {
        name: url.name,
        organization_id: url.organization_id,
        organization_name: url.organization_name
      }
    ])
  )

  const [customPersonas, setCustomPersonas] = useState<Record<string, any>>({})
  const pollCancelledRef = useRef(false)
  const [retryKey] = useState(0)
  const [accountRetryKey] = useState(0)
  const hookInputData: any[] = []

  const seenDomains = new Set<string>()
  if (data?.primary_domain) {
    hookInputData.push({
      website: data.primary_domain,
      company_id: data.id || "dummy",
      name: data.name || "Unknown"
    })
    seenDomains.add(data.primary_domain)
  }
  urls?.forEach((url: any) => {
    const domain = url.organization_domain ?? "__null__"
    if (!seenDomains.has(domain)) {
      hookInputData.push({
        website: url.organization_domain || null,
        company_id: url.organization_id || "dummy",
        name: url.organization_name || "Unknown"
      })
      seenDomains.add(domain)
    }
  })

  const {
    data: totalAccountData,
    isLoading: isAccountDetailsLoading,
    isError: isAccountBriefError
  } = useGetPreMeetingBrief(
    {
      website: data.primary_domain || urls[0]?.organization_domain,
      company_id: data.id || urls[0]?.organization_id || "dummy",
      session_id: sessionId,
      name: data.name || urls[0]?.organization_name,
      hookInputData
    },
    open,
    accountRetryKey
  )

  const {
    data: totalPersonData,
    isLoading: isPersonLoading,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isError: isPersonBriefError
  } = useGetPersonData(
    { users: linkedinData, session_id: sessionId, timestamp: timestamp },
    open && !isSingle,
    retryKey
  )

  const [streamedPerson, setStreamedPerson] = useState<any>(null)
  const [isPersonStreaming, setIsPersonStreaming] = useState(false)
  const [personStreamError, setPersonStreamError] = useState(false)
  const streamAbortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (!open || !isSingle) return
    const single = urls[0]
    if (!single?.linkedin_url_raw) return

    const controller = new AbortController()
    streamAbortRef.current = controller
    setIsPersonStreaming(true)
    setPersonStreamError(false)
    setStreamedPerson(null)

    const partial: Record<string, any> = { summary: {} }
    let receivedFrame = false
    let streamErrored = false
    getSinglePersonBriefStream(
      tokenInfo as ITokenInfo,
      single.linkedin_url_raw,
      single.name,
      single.organization_name,
      single.organization_id ? String(single.organization_id) : "",
      sessionId,
      (event, frameData) => {
        if (event === "error") {
          streamErrored = true
          setPersonStreamError(true)
          return
        }
        if (event === "done") {
          setStreamedPerson({ ...partial })
          return
        }
        receivedFrame = true
        applyPersonBriefFrame(partial, event, frameData)
        setStreamedPerson({ ...partial })
      },
      undefined,
      controller.signal
    )
      .catch((err) => {
        if ((err as { name?: string })?.name !== "AbortError") {
          streamErrored = true
          setPersonStreamError(true)
        }
      })
      .finally(() => {
        // A newer open superseded this stream — don't clobber its state.
        if (streamAbortRef.current !== controller) return
        setIsPersonStreaming(false)
        // Ended with no frames and no error frame: surface a failure instead of
        // an endless loader.
        if (!receivedFrame && !streamErrored) setPersonStreamError(true)
      })

    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isSingle, linkedinUrls[0], sessionId])

  const pollPersona = useCallback(
    async (values: any, linkedinUrl: string) => {
      if (pollCancelledRef.current) return null
      if (customPersonas[linkedinUrl]) return

      try {
        const response = await generatePersona(tokenInfo!, values)
        const isIdentified =
          response.data?.data.persona_idetification_state === "IDENTIFIED" ||
          response.data?.data.persona_idetification_state ===
            "INVALID_LINKEDIN_URL"
        if (isIdentified) {
          const personaData = response.data?.data
          setCustomPersonas((prev) => ({ ...prev, [linkedinUrl]: personaData }))
          return personaData
        }
        await new Promise((resolve) => setTimeout(resolve, 4000))
        const { promise, clear } = sleep(5000)
        await promise
        clear()
        return pollPersona(values, linkedinUrl)
      } catch {
        // Terminal marker so personaLoading resolves — otherwise the Know Your
        // Prospect capsule spins forever on a persona-fetch failure.
        setCustomPersonas((prev) => ({
          ...prev,
          [linkedinUrl]: { persona_idetification_state: "ERROR" }
        }))
      }
    },
    [customPersonas, tokenInfo]
  )

  useEffect(() => {
    if (!open) {
      pollCancelledRef.current = true
      return
    }
    pollCancelledRef.current = false
    linkedinUrls.forEach((url: string) => {
      if (!customPersonas[url]) {
        pollPersona({ linkedin_url: url, session_id: sessionId }, url)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, linkedinUrls, sessionId, pollPersona])

  const people = isSingle
    ? streamedPerson
      ? [streamedPerson]
      : undefined
    : totalPersonData?.data
  const personReady = isSingle ? !!streamedPerson : !!totalPersonData?.data
  const meetingId = totalAccountData?.data?.session_id
  const briefDisabled = isSingle
    ? isPersonStreaming || isAccountDetailsLoading
    : isPersonLoading || isAccountDetailsLoading

  const renderBody = () => {
    if (isSingle) {
      if (personStreamError) return <QueryFail />
      if (streamedPerson)
        return (
          <div data-testid="pmb-person-brief-stream" className="h-full">
            <BriefCombo
              people={streamedPerson}
              accountData={totalAccountData?.data?.results?.[0]}
              customPersonas={customPersonas}
              selectedPerson={streamedPerson}
              streaming={isPersonStreaming}
              personaLoading={!customPersonas[linkedinUrls[0]]}
            />
          </div>
        )
      return (
        <div className="p-6">
          <PeopleLoader
            name={data?.name}
            people_names={peopleNames}
            account_name={data.name}
            dataReady={isPersonStreaming}
          />
        </div>
      )
    }

    if (isPersonLoading || isAccountDetailsLoading)
      return (
        <div className="p-6">
          <PeopleLoader
            name={totalPersonData?.name}
            people_names={peopleNames}
            account_name={data.name}
            dataReady={isPersonLoading || isAccountDetailsLoading}
          />
        </div>
      )
    if (isAccountBriefError) return <QueryFail />
    if (!people) return null
    return totalPersonData?.data?.length > 1 ? (
      <MultiplePeopleInformation
        multiplePeople={totalPersonData?.data}
        isPersonLoading={isPersonLoading}
        customPersonas={customPersonas}
        totalAccountData={totalAccountData}
      />
    ) : (
      <BriefCombo
        people={totalPersonData?.data[0]}
        accountData={totalAccountData?.data?.results?.[0]}
        customPersonas={customPersonas}
        selectedPerson={people}
        personaLoading={!customPersonas[linkedinUrls[0]]}
      />
    )
  }

  return (
    <Drawer
      open={open}
      closable={false}
      width={urls?.length > 1 ? "80%" : "70%"}
      closeIcon={null}
      title={
        <div className="flex gap-4 items-center justify-between">
          <div className="flex gap-2">
            <ArrowLeftOutlined
              className="dark:text-dark-6 text-dark-2"
              onClick={() => {
                setOpen(false)
              }}
            />{" "}
            <p className="dark:text-dark-6 text-dark-2">
              {t("pre_meeting_brief.label_pre_meeting_brief")}
            </p>
          </div>
          {personReady && totalAccountData?.data && (
            <div className="flex items-center gap-4">
              <SharePreMeeting
                accountName={data.name || urls[0]?.organization_name}
                meetingId={meetingId}
                isDisabled={briefDisabled}
                peopleCount={people?.length || 0}
              />
              {!featureFlags?.hide_k_os_downloads && (
                <DownloadPreMeeting
                  accountName={formatNamesForFile(people)}
                  meetingId={meetingId}
                  isDisabled={briefDisabled}
                  peopleCount={people?.length || 0}
                />
              )}
            </div>
          )}
        </div>
      }
      footer={null}
      onClose={() => {
        streamAbortRef.current?.abort()
        setOpen(false)
      }}
      prefixCls="person-brief-drawer"
      maskClosable={true}
      classNames={{
        header: "dark:!border-border",
        body: "!p-0 overflow-hidden"
      }}
    >
      {renderBody()}
    </Drawer>
  )
}

export default PreMeetingBriefDetails
